import React, { useContext, useEffect, useState } from 'react'
import { AIResultViewModel } from './AIResultMapper'
import { Toast, ToastContainer } from 'react-bootstrap'
import classNames from 'classnames'
import { getRoomAndAIFeatureTrackingDetails } from './tracking'
import { DemoSessionContext, RoomSessionContext } from './models'
import { AIFeature } from '@zocks-communications/client-sdk-js'
import { useTracking, TrackEvent } from './common/tracking'

import { useAPIClient } from './APIClientProvider'
import { selectAuthenticatedUser } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'

export const ScoreAIResult: React.FC<{ aiFeature: AIFeature; aiResult: AIResultViewModel }> = ({ aiFeature, aiResult }) => {
  const { tracked } = useTracking()
  const user = useAppSelector(selectAuthenticatedUser)
  const roomSession = useContext(RoomSessionContext)

  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const demoContext = React.useContext(DemoSessionContext)
  const aiResultId = aiResult.id
  const { aiClient } = useAPIClient()
  useEffect(() => {
    if (!score && user) {
      setScore(aiResult.scores?.find((score) => score.userId === user.id)?.score ?? null)
    }
  }, [aiResult])

  const [score, setScore] = useState<number | null>(null)
  const _scoreAIResult = async (aiResultId: string, score: number) => {
    try {
      !demoContext.demoModeEnabled && (await aiClient.scoreAIResult(aiResultId, score))
    } catch (e: any) {
      if ('errors' in e) {
        setErrorMessage(e.errors.map((err: any) => err.message).join(', '))
      } else {
        setErrorMessage(JSON.stringify(e))
      }
      return
    }
    setScore(score)
  }

  const scoreAIResult = tracked(
    _scoreAIResult,
    {
      eventName: TrackEvent.AI_RESULT_SCORED,
      eventDetails: getRoomAndAIFeatureTrackingDetails(roomSession, aiFeature),
    },
    {
      eventDetailsExtractor: (aiResultId: string, score: number) => {
        return {
          aiResult: {
            id: aiResultId,
          },
          score,
        }
      },
    },
  )

  const errorToast = (
    <ToastContainer className='p-3' position={'top-end'} style={{ zIndex: 1 }}>
      <Toast show={!!errorMessage} onClose={() => setErrorMessage(null)} bg='danger'>
        <Toast.Header className='ondemand-error-toast'>
          <span>Scoring Error</span>
        </Toast.Header>
        <Toast.Body>{'Error during scoring the intelligence result: ' + errorMessage}</Toast.Body>
      </Toast>
    </ToastContainer>
  )

  return (
    <div className='d-flex gap-2 justify-content-end score-ai-result'>
      {errorToast}
      <div
        role='button'
        className={classNames('ai-result-score-btn score-10', { 'ai-result-score-btn--active': score && score > 7 })}
        onClick={() => scoreAIResult(aiResultId, 10)}
      >
        <i className='bi bi-emoji-smile-fill zocks-text-success'></i>
      </div>
      <div
        role='button'
        className={classNames('ai-result-score-btn score-5', {
          'ai-result-score-btn--active': score && score <= 7 && score > 3,
        })}
        onClick={() => scoreAIResult(aiResultId, 5)}
      >
        <i className='bi bi-emoji-neutral-fill zocks-text-warning'></i>
      </div>
      <div
        role='button'
        className={classNames('ai-result-score-btn score-1', { 'ai-result-score-btn--active': score && score <= 3 })}
        onClick={() => scoreAIResult(aiResultId, 1)}
      >
        <i className='bi bi-emoji-frown-fill zocks-text-error'></i>
      </div>
    </div>
  )
}
